import React from 'react';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { connect } from 'react-redux';
import { consultantSingleFetch } from '../../../lib/redux/actions/consultant';
import { hospitalConsultantCreate, hospitalConsultantUpdate, hospitalConsultantDelete } from '../../../lib/redux/actions/hospitalConsultant';
import { addToast } from '../../../lib/redux/actions/toast';
import { getSubusersConsultantToManage, isClient, isConsultant, isSeniorStaff, isStaff, isSubUser } from '../../../lib/helper/authorisation';
import HospitalTypeahead from '../../fields/HospitalTypeahead';
import { reduxForm } from 'redux-form';
import MASpinner from '../../common/MASpinner';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../../lib/routing/frontend';

const mapDispatchToProps = {
  consultantSingleFetch,
  hospitalConsultantCreate,
  hospitalConsultantUpdate,
  hospitalConsultantDelete,
  addToast,
};

class ConsultantHospitalsForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldClearTypeahead: false,
      isLoading: false,
    };
  }

  async handleHospitalAdd(selectedHospitals) {
    if (!selectedHospitals || selectedHospitals.length === 0) return;

    const { consultant } = this.props;
    const existingHospitalIds = consultant.hospitalConsultants.map((hc) => hc.hospital.id);

    // Filter out hospitals that are already added
    const newHospitals = selectedHospitals.filter((hospital) => !existingHospitalIds.includes(hospital.id));

    if (newHospitals.length === 0) {
      this.props.addToast('Hospital(s) already assigned to this consultant', false, true);
      return;
    }

    this.setState({ isLoading: true });

    try {
      // Create hospital consultant relationships for each new hospital
      for (const hospital of newHospitals) {
        await this.props.hospitalConsultantCreate({
          hospital: hospital['@id'],
          consultant: consultant['@id'],
          isDirectPay: false,
        });
      }

      // Refresh consultant data to show new relationships
      await this.props.consultantSingleFetch(consultant.id, true);

      this.setState({ shouldClearTypeahead: true, isLoading: false });
      this.props.addToast(`${newHospitals.length} hospital(s) added successfully`, true, false);
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.addToast('Failed to add hospital(s)', false, true);
    }
  }

  async handleHospitalRemove(hospitalConsultantId) {
    const { consultant } = this.props;

    this.setState({ isLoading: true });

    try {
      await this.props.hospitalConsultantDelete(hospitalConsultantId);
      await this.props.consultantSingleFetch(consultant.id, true);

      this.setState({ isLoading: false });
      this.props.addToast('Hospital removed successfully', true, false);
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.addToast('Failed to remove hospital', false, true);
    }
  }

  async handleDirectPayToggle(hospitalConsultantId, isDirectPay) {
    const { consultant } = this.props;

    this.setState({ isLoading: true });

    try {
      await this.props.hospitalConsultantUpdate(hospitalConsultantId, { isDirectPay });
      await this.props.consultantSingleFetch(consultant.id, true);

      this.setState({ isLoading: false });
      this.props.addToast('Direct pay setting updated', true, false);
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.addToast('Failed to update direct pay setting', false, true);
    }
  }

  async handleVendorNumberChange(hospitalConsultantId, vendorNumber) {
    const { consultant } = this.props;

    this.setState({ isLoading: true });

    try {
      await this.props.hospitalConsultantUpdate(hospitalConsultantId, { vendorNumber });
      await this.props.consultantSingleFetch(consultant.id, true);

      this.setState({ isLoading: false });
      this.props.addToast('Vendor number updated', true, false);
    } catch (error) {
      this.setState({ isLoading: false });
      this.props.addToast('Failed to update vendor number', false, true);
    }
  }

  typeaheadCleared() {
    this.setState({ shouldClearTypeahead: false });
  }

  getExistingHospitals() {
    const { consultant } = this.props;
    return consultant?.hospitalConsultants?.map((hc) => hc.hospital) || [];
  }

  /**
   * MED-997. What a consultant sees: the names of the hospitals their invoices relate to, and
   * nothing else.
   *
   * This restores the intent of the screen before MED-818. The hospital picker there was
   * `disabled={isClient() || isStaff(true)}`, so a consultant could read the list and change
   * nothing; the July 2025 rewrite rendered its replacement under `!isStaff(true)`, which includes
   * clients, and handed them Add, Remove, direct-pay and vendor number. Direct pay decides who a
   * hospital pays, so it is not theirs to set - but the list itself is theirs to read.
   */
  renderForClient() {
    const { consultant } = this.props;
    const hospitalConsultants = consultant?.hospitalConsultants || [];

    return (
      <div className="crud__section mb-3 bg-white">
        <div className="crud__section-icon">
          <i className="fas fa-first-aid" />
        </div>
        <h2 className="crud__section-header">{isConsultant(true) ? 'Your ' : getSubusersConsultantToManage().name + "'s "}Hospitals</h2>
        <p className="hint-text">The Hospitals related to {isConsultant(true) ? 'Your ' : getSubusersConsultantToManage().name + "'s "}Invoices.</p>

        {hospitalConsultants.length > 0 ? (
          <ul className="list-unstyled mb-0">
            {hospitalConsultants.map((hospitalConsultant) => (
              <li key={`hospital-consultant-${hospitalConsultant.id}`} className="mb-1">
                <strong>{hospitalConsultant.hospital.name}</strong>
              </li>
            ))}
          </ul>
        ) : (
          <div className="alert alert-info mb-0">
            <i className="fas fa-info-circle" /> No hospitals assigned yet.
          </div>
        )}
      </div>
    );
  }

  render() {
    const { consultant } = this.props;
    const { shouldClearTypeahead, isLoading } = this.state;
    const hospitalConsultants = consultant?.hospitalConsultants || [];

    if (isClient()) {
      return this.renderForClient();
    }

    return (
      <div className="crud__section mb-3 bg-white">
        <div className="crud__section-icon">
          <i className="fas fa-first-aid" />
        </div>
        <h2 className="crud__section-header">{isConsultant(true) ? 'Your ' : isSubUser(true) ? getSubusersConsultantToManage().name + "'s " : ''}Hospitals</h2>
        <p className="hint-text">
          Add/Remove Hospitals related to {isConsultant(true) ? 'Your ' : isSubUser(true) ? getSubusersConsultantToManage().name + "'s " : 'this Consultants '}
          Invoices
        </p>

        {isSeniorStaff() && (
          <div className="mb-3">
            <HospitalTypeahead
              shouldClear={shouldClearTypeahead}
              hasCleared={this.typeaheadCleared.bind(this)}
              onChange={this.handleHospitalAdd.bind(this)}
              selectedHospitals={this.getExistingHospitals()}
              label="Add Hospital"
              name="hospitalSelector"
              multiple={true}
              disabled={isLoading}
            />
          </div>
        )}

        {hospitalConsultants.length > 0 ? (
          <Table size="sm">
            <thead>
              <tr>
                <th className="text-left">Hospital</th>
                <th className="text-left">Direct Pay</th>
                <th className="text-left">Vendor Number</th>
                {isSeniorStaff() && <th className="text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {hospitalConsultants.map((hospitalConsultant) => (
                <tr key={`hospital-consultant-${hospitalConsultant.id}`}>
                  <td>
                    <strong>{hospitalConsultant.hospital.name}</strong>
                  </td>
                  <td className="text-center">
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="switch"
                        id={`direct-pay-${hospitalConsultant.id}`}
                        checked={hospitalConsultant.isDirectPay}
                        onChange={(e) => this.handleDirectPayToggle(hospitalConsultant.id, e.target.checked)}
                        disabled={isStaff(true) || isLoading}
                      />
                      <span className={`text-${hospitalConsultant.isDirectPay ? 'success' : 'muted'}`}>
                        {hospitalConsultant.isDirectPay ? 'Direct Pay' : 'MediAccount Payment'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Enter vendor number"
                      defaultValue={hospitalConsultant.vendorNumber || ''}
                      onBlur={(e) => {
                        const newValue = e.target.value.trim();
                        if (newValue !== (hospitalConsultant.vendorNumber || '')) {
                          this.handleVendorNumberChange(hospitalConsultant.id, newValue || null);
                        }
                      }}
                      disabled={isStaff(true) || isLoading}
                      style={{ maxWidth: '200px' }}
                    />
                  </td>
                  {isSeniorStaff() && (
                    <td className="text-center">
                      <Link
                        to={ROUTES.HOSPITALS.SINGLE.replace(':id', hospitalConsultant.hospital.id)}
                        className="btn btn-sm btn-outline-primary mr-2"
                        title={`View ${hospitalConsultant.hospital.name}`}>
                        <i className="fas fa-eye mr-1" />
                        View
                      </Link>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Remove Hospital"
                        onClick={() => this.handleHospitalRemove(hospitalConsultant.id)}
                        disabled={isLoading}>
                        <i className="fas fa-times" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" /> No hospitals assigned to this consultant yet.
          </div>
        )}

        {isLoading && (
          <div className="text-center mt-3">
            <MASpinner msg="Processing..." />
          </div>
        )}
      </div>
    );
  }
}

export default connect(
  null,
  mapDispatchToProps,
)(
  reduxForm({
    form: 'ConsultantHospitalsForm',
  })(ConsultantHospitalsForm),
);
